// Copyright (c) 2020 Comsearch, Inc., All rights reserved

import { useCallback, useEffect, useRef, useState } from 'react'

const useAnalysisStatusesWrapper = ({ closeModal, errorErrorsList, getUsersAnalyses }) => {
  const __fetchErrorCount = useRef(0)
  const __timeoutId = useRef(null)

  const [state, setState] = useState({
    isLoading: true,
    usersAnalyses: [],
  })

  const getAnalysisData = useCallback(async () => {
    try {
      const {
        pollForUpdates,
        usersAnalyses,
      } = await getUsersAnalyses()

      setState(s => ({
        ...s,
        usersAnalyses,
      }))

      // if something is waiting or running, tell this to continue polling
      if (pollForUpdates) {
        __timeoutId.current = setTimeout(getAnalysisData, 3000)
      }
    } catch (err) {
      // keep track of how many successive 502/503/504 errors there are
      // if they are fewer than 10, then continue polling.
      if (
        __fetchErrorCount.current < 10 &&
        (err.status === 502 || err.status === 503 || err.status === 504)
      ) {
        __fetchErrorCount.current += 1
        __timeoutId.current = setTimeout(getAnalysisData, 3000)
        return
      }

      // if this failed for any reason, just get outta here
      console.warn(err)

      // if we're here, that means we want to hide the document generation notification and
      // instead display an error notification describing what went wrong.
      errorErrorsList(err, { autoDismiss: 5 })

      closeModal()

      // to prevent the caller from continuing.
      throw err
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      try {
        await getAnalysisData()
        setState(s => ({
          ...s,
          isLoading: false,
        }))
      } catch (err) {
        // we've already handled the error in getAnalysisData.
        // we just want to be able to prevent setState on an unmounted component.
        return
      }
    }
    init()

    return () => {
      clearTimeout(__timeoutId.current)
    }
  }, [])

  return state
}

export default useAnalysisStatusesWrapper
